import { createSelector } from 'reselect';
import Immutable from 'immutable';

// Selectors
import { breadcrumbSelector } from '../../workspace/breadcrumbs/breadcrumbSelector';
import { boardVisibleElementGraphSelector } from '../selectors/elementGraphSelector';
import {
    getIsAsyncResourceEntityFetched,
    getIsAsyncResourceEntityFetching,
} from '../../utils/services/http/asyncResource/asyncResourceSelector';

// Utils
import { getElements } from '../utils/elementStoreUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getLabels, getTitle } from '../../../common/elements/utils/elementPropertyUtils';

import { prop } from '../../../common/utils/immutableHelper';
import { getUniqueArray } from '../../../common/utils/arrayUtils';
import { selectSelectedElementChip } from '../chipsPanel/elementChipsSelectors';

// Types
import { Label, LabelMap, LabelsByBoards, SelectedLabel } from '../../../common/labels/labelTypes';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';
import { getCurrentUserRootBoardId } from '../../user/currentUserSelector';

/**
 * Returns the raw label state object from Redux.
 */
const getLabelState = (state: Immutable.Map<string, unknown>) => prop('labels', state);

/**
 * Returns every label known to the client, including read-only labels.
 */
export const selectAllLabels = (state: Immutable.Map<string, unknown>): LabelMap | undefined =>
    prop('currentUserLabels', getLabelState(state));

/**
 * Returns only labels that are usable by the caller.
 */
export const selectUsableLabels = createSelector(selectAllLabels, (labels): LabelMap | undefined => {
    if (!labels) return undefined;

    return Object.keys(labels).reduce((usableLabels, labelId) => {
        if (labels[labelId]?.readOnly) return usableLabels;

        usableLabels[labelId] = labels[labelId];
        return usableLabels;
    }, {} as LabelMap);
});

export const selectLabelsByIds =
    (labelIds: Immutable.List<string>) =>
    (state: Immutable.Map<string, object>): Immutable.List<Label> | undefined => {
        if (!labelIds || labelIds.size === 0) return;

        const allLabels = selectAllLabels(state) || {};
        return labelIds.map((labelId) => labelId && allLabels[labelId]).filter(Boolean) as Immutable.List<Label>;
    };

export const getSelectedLabel = (state: Immutable.Map<string, unknown>): SelectedLabel | undefined => {
    const labelState = getLabelState(state);
    if (!labelState) return undefined;

    return prop('selectedLabel', labelState);
};

export const getSelectedLabelId = (state: Immutable.Map<string, unknown>): string | undefined => {
    const selectedLabel = getSelectedLabel(state);
    return selectedLabel?._id;
};

export const getSelectedLabelIdForElement =
    (elementId: string) =>
    (state: Immutable.Map<string, unknown>): string | undefined => {
        const selectedLabel = getSelectedLabel(state);
        if (selectedLabel?.elementId !== elementId) return undefined;

        return selectedLabel?._id;
    };

/**
 * Returns the active label id from either the new element-pill selection or the legacy label slice.
 *
 * Both pill architectures run side by side during the transition: element types still on the legacy
 * `LabelElementPillsContainer` populate the `labels` slice, while pill-migrated surfaces populate the
 * `elementChips` slice. Toolbar tooling reads through this so it works regardless of which path is active.
 */
export const getActiveToolbarLabelId = (state: Immutable.Map<string, unknown>): string | undefined => {
    const selectedChip = selectSelectedElementChip(state);
    if (selectedChip?.chip.type === 'label') return selectedChip.chip.label._id;

    return getSelectedLabelId(state);
};

/**
 * Returns the full active label from either the new element-pill selection or the legacy label slice.
 * See {@link getActiveToolbarLabelId} for why both sources are supported.
 */
export const getActiveToolbarLabel = (state: Immutable.Map<string, unknown>): Label | undefined => {
    const selectedChip = selectSelectedElementChip(state);
    if (selectedChip?.chip.type === 'label') return selectedChip.chip.label;

    const selectedLabelId = getSelectedLabelId(state);
    if (!selectedLabelId) return undefined;

    return selectUsableLabels(state)?.[selectedLabelId];
};

export const getEditingLabelId = (state: Immutable.Map<string, unknown>): string | null => {
    const labelState = getLabelState(state);
    if (!labelState) return null;

    return prop('editingLabelId', labelState) ?? null;
};

export const getLabelsStaleTimestamp = (state: Immutable.Map<string, unknown>): number | null => {
    const labelState = getLabelState(state);
    if (!labelState) return null;

    return prop('staleTimestamp', labelState) ?? null;
};

export const getFullLabelFetchTimestamp = (state: Immutable.Map<string, unknown>): number | null => {
    const labelState = getLabelState(state);
    if (!labelState) return null;

    return prop('fullLabelFetchTimestamp', labelState) ?? null;
};

export const getLabelsAreStale = (state: Immutable.Map<string, unknown>): boolean =>
    getLabelsStaleTimestamp(state) !== null;

export const selectBoardsToFetchForCurrentPath = createSelector(
    breadcrumbSelector,
    (state: Immutable.Map<string, unknown>) => state,
    (breadcrumbs, state): { toFetch: string[]; isBoardFetchInProgress: boolean } => {
        const toFetch: string[] = [];
        let isBoardFetchInProgress = false;

        breadcrumbs?.path?.forEach((boardId: string) => {
            if (getIsAsyncResourceEntityFetching(state, ResourceTypes.boards, boardId)) {
                if (!isBoardFetchInProgress) isBoardFetchInProgress = true;
                return;
            }

            if (!getIsAsyncResourceEntityFetched(state, ResourceTypes.boards, boardId)) toFetch.push(boardId);
        });

        return {
            toFetch,
            isBoardFetchInProgress,
        };
    },
);

export const selectCurrentPathLabels = createSelector(
    breadcrumbSelector,
    getElements,
    boardVisibleElementGraphSelector,
    selectUsableLabels,
    getCurrentUserRootBoardId,
    (breadcrumbs, elements, visibleElementsGraph, labelMap = {}, rootBoardId): LabelsByBoards => {
        const usedLabelIds = new Set<string>();

        return (
            breadcrumbs?.path?.reduce((acc: LabelsByBoards, boardId: string): LabelsByBoards => {
                const visibleElements = visibleElementsGraph?.[boardId] || [];
                const boardLabelIds: string[] = [];

                visibleElements.forEach((elementId) => {
                    const element = getElement(elements, elementId);
                    const labels = getLabels(element);
                    if (!labels || !labels.length) return;

                    boardLabelIds.push(...labels);
                });

                const uniqueBoardLabelIds = getUniqueArray(boardLabelIds).filter(
                    (labelId: string) => !usedLabelIds.has(labelId),
                );

                if (!uniqueBoardLabelIds.length) return acc;

                uniqueBoardLabelIds.forEach((labelId) => usedLabelIds.add(labelId));

                const board = getElement(elements, boardId);
                const boardLabels = uniqueBoardLabelIds.map((labelId) => labelMap[labelId]).filter(Boolean) as Label[];
                if (!boardLabels.length) return acc;

                acc.push({
                    id: boardId,
                    title: getTitle(board),
                    isRoot: boardId === rootBoardId,
                    labels: boardLabels,
                });

                return acc;
            }, []) || []
        );
    },
);

export const getElementIdForSelectedLabel = (state: Immutable.Map<string, unknown>): string | undefined => {
    const selectedLabel = getSelectedLabel(state);
    return selectedLabel?.selectedElementId || selectedLabel?.elementId;
};
